/**
 * EHR Interoperability Configuration Panel
 * 
 * Admin interface for configuring external EHR system connections.
 * Supports Epic, Cerner, Meditech, Allscripts via FHIR R4 and HL7 ADT.
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Server, Link, Unlink, Settings, CheckCircle, XCircle,
  AlertTriangle, RefreshCw, Loader2, Plus, Trash2, Edit,
  Database, Shield, Activity, Clock, FileText, Users
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// EHR System logos/icons
const EHR_SYSTEMS = {
  epic: {
    name: "Epic",
    logo: "🏥",
    color: "text-red-400",
    bg: "bg-red-500/10",
    description: "Epic MyChart and EpicCare integration"
  },
  cerner: {
    name: "Cerner",
    logo: "🔷",
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    description: "Cerner Millennium and PowerChart"
  },
  meditech: {
    name: "Meditech",
    logo: "🟢",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    description: "Meditech Expanse integration"
  },
  allscripts: {
    name: "Allscripts",
    logo: "🟣",
    color: "text-purple-400",
    bg: "bg-purple-500/10",
    description: "Allscripts Sunrise and TouchWorks"
  }
};

export default function EHRInteropConfig({ isDark = true }) {
  const token = localStorage.getItem("token");
  
  // States
  const [supportedSystems, setSupportedSystems] = useState({});
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [activeTab, setActiveTab] = useState("connections");
  const [connectionLogs, setConnectionLogs] = useState([]);
  
  // New configuration form
  const [newConfig, setNewConfig] = useState({
    name: "",
    system_type: "",
    base_url: "",
    client_id: "",
    client_secret: "",
    api_key: "",
    enabled: true,
    auto_sync: false,
    sync_interval_minutes: 60
  });

  // Fetch supported systems and configurations
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch supported systems
        const systemsRes = await fetch(`${API_URL}/api/interoperability/supported-systems`);
        if (systemsRes.ok) {
          const data = await systemsRes.json();
          setSupportedSystems(data.systems || {});
        }
        
        // Fetch configurations
        const configsRes = await fetch(`${API_URL}/api/interoperability/configurations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (configsRes.ok) {
          const data = await configsRes.json();
          setConfigurations(data.configurations || []);
        }
        
      } catch (error) {
        console.error("Failed to fetch EHR data:", error);
        toast.error("Failed to load EHR configurations");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Test connection
  const testConnection = async (configId) => {
    try {
      setTesting(configId);
      const res = await fetch(`${API_URL}/api/interoperability/test-connection/${configId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success("Connection successful!");
          // Update configuration status
          setConfigurations(prev => prev.map(c => 
            c.id === configId ? { ...c, status: "connected", last_tested: new Date().toISOString() } : c
          ));
        } else {
          toast.error(`Connection failed: ${data.error || "Unknown error"}`);
        }
      }
    } catch (error) {
      toast.error("Connection test failed");
    } finally {
      setTesting(null);
    }
  };

  // Save configuration
  const saveConfiguration = async () => {
    if (!newConfig.name || !newConfig.system_type || !newConfig.base_url) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/interoperability/configurations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newConfig)
      });

      if (res.ok) {
        const data = await res.json();
        setConfigurations(prev => [...prev, data.configuration]);
        setShowAddDialog(false);
        setNewConfig({
          name: "",
          system_type: "",
          base_url: "",
          client_id: "",
          client_secret: "",
          api_key: "",
          enabled: true,
          auto_sync: false,
          sync_interval_minutes: 60
        });
        toast.success("Configuration saved successfully");
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  // Toggle configuration enabled
  const toggleEnabled = async (configId, enabled) => {
    try {
      const res = await fetch(`${API_URL}/api/interoperability/configurations/${configId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled })
      });

      if (res.ok) {
        setConfigurations(prev => prev.map(c => 
          c.id === configId ? { ...c, enabled } : c
        ));
        toast.success(`Connection ${enabled ? "enabled" : "disabled"}`);
      }
    } catch (error) {
      toast.error("Failed to update configuration");
    }
  };

  // Delete configuration
  const deleteConfiguration = async (configId) => {
    if (!confirm("Are you sure you want to delete this configuration?")) return;

    try {
      const res = await fetch(`${API_URL}/api/interoperability/configurations/${configId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setConfigurations(prev => prev.filter(c => c.id !== configId));
        toast.success("Configuration deleted");
      }
    } catch (error) {
      toast.error("Failed to delete configuration");
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "connected":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
          <CheckCircle className="w-3 h-3 mr-1" /> Connected
        </Badge>;
      case "error":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
          <XCircle className="w-3 h-3 mr-1" /> Error
        </Badge>;
      case "pending":
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
          <AlertTriangle className="w-3 h-3 mr-1" /> Pending
        </Badge>;
      default:
        return <Badge variant="outline">
          <Clock className="w-3 h-3 mr-1" /> Not Tested
        </Badge>;
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950" : "bg-gray-50"}`} data-testid="ehr-interop-config">
      {/* Header */}
      <div className={`border-b ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"} sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}>
                <Server className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  EHR Interoperability
                </h1>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Configure external EHR system connections
                </p>
              </div>
            </div>
            
            <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Connection
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={isDark ? "bg-slate-800" : ""}>
            <TabsTrigger value="connections">
              <Link className="w-4 h-4 mr-2" />
              Connections
            </TabsTrigger>
            <TabsTrigger value="systems">
              <Database className="w-4 h-4 mr-2" />
              Supported Systems
            </TabsTrigger>
            <TabsTrigger value="logs">
              <FileText className="w-4 h-4 mr-2" />
              Activity Logs
            </TabsTrigger>
          </TabsList>

          {/* Connections Tab */}
          <TabsContent value="connections" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : configurations.length > 0 ? (
              <div className="grid gap-4">
                {configurations.map((config) => {
                  const system = EHR_SYSTEMS[config.system_type] || {
                    name: config.system_type,
                    logo: "🔗",
                    color: "text-slate-400",
                    bg: "bg-slate-500/10"
                  };
                  
                  return (
                    <Card key={config.id} className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg text-2xl ${system.bg}`}>
                              {system.logo}
                            </div>
                            <div>
                              <div className="flex items-center gap-3">
                                <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                                  {config.name}
                                </h3>
                                {getStatusBadge(config.status)}
                                {!config.enabled && (
                                  <Badge variant="outline" className="text-slate-400">Disabled</Badge>
                                )}
                              </div>
                              <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                                {system.name} • {config.base_url}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs">
                                {config.auto_sync && (
                                  <span className="flex items-center gap-1 text-emerald-400">
                                    <RefreshCw className="w-3 h-3" />
                                    Auto-sync every {config.sync_interval_minutes}m
                                  </span>
                                )}
                                {config.last_tested && (
                                  <span className={isDark ? "text-slate-500" : "text-gray-400"}>
                                    Last tested: {new Date(config.last_tested).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Switch 
                              checked={config.enabled}
                              onCheckedChange={(checked) => toggleEnabled(config.id, checked)}
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => testConnection(config.id)}
                              disabled={testing === config.id}
                            >
                              {testing === config.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Activity className="w-4 h-4 mr-1" />
                              )}
                              Test
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setEditingConfig(config);
                                setNewConfig(config);
                                setShowAddDialog(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => deleteConfiguration(config.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardContent className="py-12 text-center">
                  <Unlink className={`w-12 h-12 mx-auto mb-4 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
                  <h3 className={`text-lg font-medium mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                    No EHR Connections Configured
                  </h3>
                  <p className={`text-sm mb-4 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    Connect to external EHR systems to enable patient data exchange
                  </p>
                  <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Connection
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Supported Systems Tab */}
          <TabsContent value="systems" className="mt-6">
            <div className="grid grid-cols-2 gap-6">
              {Object.entries(EHR_SYSTEMS).map(([key, system]) => {
                const systemInfo = supportedSystems[key] || {};
                
                return (
                  <Card key={key} className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-lg text-3xl ${system.bg}`}>
                          {system.logo}
                        </div>
                        <div>
                          <CardTitle className={`text-lg ${system.color}`}>{system.name}</CardTitle>
                          <CardDescription>{system.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <h4 className={`text-sm font-medium mb-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                            Supported Protocols
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {(systemInfo.protocols || ["FHIR R4", "HL7 ADT"]).map((protocol, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {protocol}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className={`text-sm font-medium mb-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                            FHIR Resources
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {(systemInfo.fhir_resources || ["Patient", "Condition", "MedicationStatement"]).map((resource, idx) => (
                              <Badge key={idx} className="bg-blue-500/20 text-blue-400 text-xs">
                                {resource}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className={`text-sm font-medium mb-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                            ADT Events
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {["A01 Admit", "A02 Transfer", "A03 Discharge", "A08 Update"].map((event, idx) => (
                              <Badge key={idx} className="bg-purple-500/20 text-purple-400 text-xs">
                                {event}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="logs" className="mt-6">
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Connection Activity Logs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>System</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Demo logs */}
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableCell className="text-sm">{new Date().toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline">Epic</Badge></TableCell>
                      <TableCell>Patient Import</TableCell>
                      <TableCell><Badge className="bg-emerald-500/20 text-emerald-400">Success</Badge></TableCell>
                      <TableCell className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Imported 12 patient records
                      </TableCell>
                    </TableRow>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableCell className="text-sm">{new Date(Date.now() - 3600000).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline">Cerner</Badge></TableCell>
                      <TableCell>ADT A01 Received</TableCell>
                      <TableCell><Badge className="bg-emerald-500/20 text-emerald-400">Processed</Badge></TableCell>
                      <TableCell className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        New admission processed
                      </TableCell>
                    </TableRow>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableCell className="text-sm">{new Date(Date.now() - 7200000).toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline">Epic</Badge></TableCell>
                      <TableCell>Connection Test</TableCell>
                      <TableCell><Badge className="bg-emerald-500/20 text-emerald-400">Success</Badge></TableCell>
                      <TableCell className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        FHIR endpoint verified
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Configuration Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setEditingConfig(null);
          setNewConfig({
            name: "",
            system_type: "",
            base_url: "",
            client_id: "",
            client_secret: "",
            api_key: "",
            enabled: true,
            auto_sync: false,
            sync_interval_minutes: 60
          });
        }
      }}>
        <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-400" />
              {editingConfig ? "Edit EHR Connection" : "Add EHR Connection"}
            </DialogTitle>
            <DialogDescription>
              Configure connection to an external EHR system via FHIR R4 or HL7 ADT
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Connection Name *</Label>
                <Input 
                  value={newConfig.name}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Hospital Epic"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div>
                <Label className="text-xs">EHR System *</Label>
                <Select 
                  value={newConfig.system_type}
                  onValueChange={(v) => setNewConfig(prev => ({ ...prev, system_type: v }))}
                >
                  <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <SelectValue placeholder="Select system" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EHR_SYSTEMS).map(([key, system]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span>{system.logo}</span>
                          {system.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Base URL / FHIR Endpoint *</Label>
              <Input 
                value={newConfig.base_url}
                onChange={(e) => setNewConfig(prev => ({ ...prev, base_url: e.target.value }))}
                placeholder="https://fhir.hospital.org/api/FHIR/R4"
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Client ID</Label>
                <Input 
                  value={newConfig.client_id}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, client_id: e.target.value }))}
                  placeholder="OAuth Client ID"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div>
                <Label className="text-xs">Client Secret</Label>
                <Input 
                  type="password"
                  value={newConfig.client_secret}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                  placeholder="OAuth Client Secret"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">API Key (if required)</Label>
              <Input 
                type="password"
                value={newConfig.api_key}
                onChange={(e) => setNewConfig(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="API Key"
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>

            <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              <h4 className={`text-sm font-medium mb-3 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                Sync Options
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Enable Connection</Label>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                      Allow data exchange with this system
                    </p>
                  </div>
                  <Switch 
                    checked={newConfig.enabled}
                    onCheckedChange={(checked) => setNewConfig(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Auto-Sync</Label>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                      Automatically sync patient data
                    </p>
                  </div>
                  <Switch 
                    checked={newConfig.auto_sync}
                    onCheckedChange={(checked) => setNewConfig(prev => ({ ...prev, auto_sync: checked }))}
                  />
                </div>
                {newConfig.auto_sync && (
                  <div className="flex items-center gap-4">
                    <Label className="text-sm">Sync Interval</Label>
                    <Select 
                      value={String(newConfig.sync_interval_minutes)}
                      onValueChange={(v) => setNewConfig(prev => ({ ...prev, sync_interval_minutes: parseInt(v) }))}
                    >
                      <SelectTrigger className={`w-32 ${isDark ? "bg-slate-900 border-slate-700" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="240">4 hours</SelectItem>
                        <SelectItem value="1440">24 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveConfiguration}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {editingConfig ? "Update Connection" : "Add Connection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
